import { useNavigate } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import {
    Shield,
    Target,
    Share2,
    Map as MapIcon,
    Cpu,
    ArrowRight,
    Activity,
    CheckCircle2,
    Zap,
    Lock
} from 'lucide-react';

export default function WelcomePage() {
    const navigate = useNavigate();
    const { t } = useTranslation();

    return (
        <div className="space-y-12 animate-in fade-in duration-700">
            {/* Hero Section */}
            <div className="relative overflow-hidden rounded-[3rem] bg-gradient-to-br from-blue-900/40 via-[#050608] to-gray-900/40 border border-blue-500/10 shadow-2xl">
                <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-600/20 blur-[120px] rounded-full -mr-32 -mt-32 pointer-events-none" />
                <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-purple-600/10 blur-[120px] rounded-full -ml-32 -mb-32 pointer-events-none" />

                <div className="relative z-10 p-12 md:p-20 text-center">
                    <div className="inline-flex items-center justify-center p-3 sm:p-4 rounded-3xl bg-blue-500/20 text-blue-400 mb-8 border border-blue-500/20 shadow-lg shadow-blue-500/10">
                        <Shield className="w-10 h-10 sm:w-16 sm:h-16" />
                    </div>

                    <h1 className="text-4xl md:text-6xl font-black text-white tracking-tighter mb-4 uppercase">
                        VANTAK
                    </h1>
                    <p className="text-xl md:text-2xl font-light text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-500 mb-6">
                        {t('welcome.tagline')}
                    </p>

                    <p className="text-lg md:text-xl text-gray-400 max-w-3xl mx-auto leading-relaxed font-light">
                        <Trans
                            i18nKey="welcome.heroDescription"
                            defaults="La plataforma SaaS para organizaciones de seguridad privada, protección corporativa y equipos de respuesta que necesitan <1>coordinación operacional precisa</1>, deconflicto geoespacial en tiempo real y trazabilidad completa."
                            components={{ 1: <strong className="text-white" /> }}
                        />
                    </p>

                    <div className="mt-10 flex flex-wrap justify-center gap-4">
                        <button
                            onClick={() => navigate('/dashboard')}
                            className="px-8 py-4 bg-blue-600 hover:bg-blue-500 text-white rounded-2xl font-bold uppercase tracking-widest transition-all shadow-lg hover:shadow-blue-500/25 flex items-center gap-2 group"
                        >
                            {t('welcome.goToDashboard')}
                            <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Value Props */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Para quién es */}
                <div className="p-10 rounded-[2.5rem] bg-gray-900/40 border border-white/5 relative overflow-hidden group hover:border-blue-500/20 transition-all">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/5 rounded-full -mr-20 -mt-20 blur-3xl group-hover:bg-blue-500/10 transition-colors" />

                    <h3 className="text-xl font-black text-white uppercase tracking-wider mb-6 flex items-center gap-3">
                        <div className="p-2 bg-blue-500/20 rounded-lg text-blue-400"><Cpu className="w-5 h-5" /></div>
                        {t('welcome.designedFor')}
                    </h3>

                    <div className="space-y-4 text-gray-400 leading-relaxed font-light">
                        {[
                            'welcome.designedForItems.0',
                            'welcome.designedForItems.1',
                            'welcome.designedForItems.2',
                            'welcome.designedForItems.3',
                            'welcome.designedForItems.4',
                        ].map((key, idx) => (
                            <div key={idx} className="flex items-start gap-3 text-sm text-gray-300">
                                <CheckCircle2 className="w-4 h-4 text-blue-400 mt-0.5 shrink-0" />
                                {t(key)}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Diferenciadores */}
                <div className="p-10 rounded-[2.5rem] bg-gray-900/40 border border-white/5 relative overflow-hidden group hover:border-emerald-500/20 transition-all">
                    <div className="absolute bottom-0 left-0 w-64 h-64 bg-emerald-500/5 rounded-full -ml-20 -mb-20 blur-3xl group-hover:bg-emerald-500/10 transition-colors" />

                    <h3 className="text-xl font-black text-white uppercase tracking-wider mb-6 flex items-center gap-3">
                        <div className="p-2 bg-emerald-500/20 rounded-lg text-emerald-400"><Zap className="w-5 h-5" /></div>
                        {t('welcome.whyVantak')}
                    </h3>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {[
                            'welcome.whyVantakItems.0',
                            'welcome.whyVantakItems.1',
                            'welcome.whyVantakItems.2',
                            'welcome.whyVantakItems.3',
                            'welcome.whyVantakItems.4',
                            'welcome.whyVantakItems.5',
                        ].map((key, idx) => (
                            <div key={idx} className="flex items-center gap-3 text-sm text-gray-300">
                                <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
                                {t(key)}
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Use Cases */}
            <div className="p-10 rounded-[2.5rem] bg-white/[0.02] border border-white/5">
                <h2 className="text-2xl font-black text-white uppercase tracking-tight mb-8 flex items-center gap-3">
                    <Target className="w-6 h-6 text-rose-500" />
                    {t('welcome.operationScenarios')}
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {[
                        { titleKey: 'welcome.useCases.corporate.title', descKey: 'welcome.useCases.corporate.desc', color: 'rose' },
                        { titleKey: 'welcome.useCases.massEvents.title', descKey: 'welcome.useCases.massEvents.desc', color: 'amber' },
                        { titleKey: 'welcome.useCases.crisis.title', descKey: 'welcome.useCases.crisis.desc', color: 'blue' },
                        { titleKey: 'welcome.useCases.patrol.title', descKey: 'welcome.useCases.patrol.desc', color: 'purple' }
                    ].map((dim, idx) => (
                        <div key={idx} className="p-6 rounded-2xl bg-gray-900/50 border border-white/5 hover:border-white/10 transition-colors">
                            <h4 className={`text-lg font-bold text-${dim.color}-400 mb-2 uppercase text-sm tracking-wider`}>{t(dim.titleKey)}</h4>
                            <p className="text-sm text-gray-400 leading-relaxed font-light">
                                {t(dim.descKey)}
                            </p>
                        </div>
                    ))}
                </div>
            </div>

            {/* Core Pillars */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="p-8 rounded-[2rem] bg-gray-900/40 border border-white/5 hover:border-blue-500/30 transition-all group">
                    <div className="w-12 h-12 rounded-2xl bg-indigo-500/20 text-indigo-400 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                        <Share2 className="w-6 h-6" />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-3">{t('welcome.totalCoordination')}</h3>
                    <p className="text-gray-400 leading-relaxed">
                        {t('welcome.totalCoordinationDesc')}
                    </p>
                </div>

                <div className="p-8 rounded-[2rem] bg-gray-900/40 border border-white/5 hover:border-emerald-500/30 transition-all group">
                    <div className="w-12 h-12 rounded-2xl bg-emerald-500/20 text-emerald-400 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                        <MapIcon className="w-6 h-6" />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-3">{t('welcome.tacticalIntelligence')}</h3>
                    <p className="text-gray-400 leading-relaxed">
                        {t('welcome.tacticalIntelligenceDesc')}
                    </p>
                </div>

                <div className="p-8 rounded-[2rem] bg-gray-900/40 border border-white/5 hover:border-rose-500/30 transition-all group">
                    <div className="w-12 h-12 rounded-2xl bg-rose-500/20 text-rose-400 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                        <Lock className="w-6 h-6" />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-3">{t('welcome.totalIsolation')}</h3>
                    <p className="text-gray-400 leading-relaxed">
                        {t('welcome.totalIsolationDesc')}
                    </p>
                </div>
            </div>

            {/* Capabilities */}
            <div className="p-10 rounded-[2.5rem] border border-white/10 bg-white/[0.02]">
                <h2 className="text-2xl font-black text-white uppercase tracking-tight mb-8 flex items-center gap-3">
                    <Cpu className="w-6 h-6 text-blue-500" />
                    {t('welcome.platformCapabilities')}
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                    <div className="space-y-4">
                        <div className="h-1 w-12 bg-blue-500 rounded-full" />
                        <h4 className="text-lg font-bold text-gray-200">{t('welcome.capabilities.geoDeconfliction.title')}</h4>
                        <p className="text-sm text-gray-500">
                            {t('welcome.capabilities.geoDeconfliction.desc')}
                        </p>
                    </div>

                    <div className="space-y-4">
                        <div className="h-1 w-12 bg-purple-500 rounded-full" />
                        <h4 className="text-lg font-bold text-gray-200">{t('welcome.capabilities.mobileApp.title')}</h4>
                        <p className="text-sm text-gray-500">
                            {t('welcome.capabilities.mobileApp.desc')}
                        </p>
                    </div>

                    <div className="space-y-4">
                        <div className="h-1 w-12 bg-amber-500 rounded-full" />
                        <h4 className="text-lg font-bold text-gray-200">{t('welcome.capabilities.audit.title')}</h4>
                        <p className="text-sm text-gray-500">
                            {t('welcome.capabilities.audit.desc')}
                        </p>
                    </div>

                    <div className="space-y-4">
                        <div className="h-1 w-12 bg-green-500 rounded-full" />
                        <h4 className="text-lg font-bold text-gray-200">{t('welcome.capabilities.liveDashboard.title')}</h4>
                        <p className="text-sm text-gray-500">
                            {t('welcome.capabilities.liveDashboard.desc')}
                        </p>
                    </div>
                </div>
            </div>

            {/* Footer */}
            <div className="text-center py-8 opacity-60">
                <p className="text-sm font-mono text-gray-500 uppercase tracking-[0.2em] flex items-center justify-center gap-3">
                    <Activity className="w-4 h-4" />
                    {t('welcome.footerTagline')}
                    <Activity className="w-4 h-4" />
                </p>
            </div>
        </div>
    );
}
